import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'
import useBroadcastWeekDays from 'core/hooks/useBroadcastWeekDays'
import { currentDate } from 'core/store/broadcastTable'
import { formatYYYYMMDD } from 'core/util/date'

interface StyledWeekProps {
  isPrevDay: boolean
  isToday: boolean
  isNextDay: boolean
  disabled?: boolean
  isSelected: boolean
}

interface BroadcastWeekDaysProps {
  setIsEmpty: React.Dispatch<React.SetStateAction<boolean>>
}

const BroadcastWeekDays: React.FC<BroadcastWeekDaysProps> = ({ setIsEmpty }) => {
  const broadcastTableRef = useRef<HTMLDivElement>(null)
  const { weekDays, onClickWeek, broadcastCount } = useBroadcastWeekDays()

  const handleClickWeek = ({ date, isEmpty }: { date: Date; isEmpty: boolean }) => {
    onClickWeek(date)
    setIsEmpty(isEmpty)
  }

  useEffect(() => {
    const isTodayEmpty = broadcastCount
      ? broadcastCount[formatYYYYMMDD({ date: currentDate })] === 0
      : true
    setIsEmpty(isTodayEmpty)
  }, [broadcastCount, setIsEmpty])

  /** 스크롤 이벤트시 모든 이벤트 무시 추가 */
  useEffect(() => {
    if (!broadcastTableRef.current) return

    const stopAllPropagation = (e: Event) => {
      e.stopPropagation()
    }
    const currentRef = broadcastTableRef.current

    const eventTypes = [
      'dblclick',
      'mousedown',
      'mouseup',
      'mouseover',
      'mouseout',
      'mousemove',
      'touchstart',
      'touchend',
      'touchmove',
      'touchcancel',
      'keydown',
      'keyup',
      'keypress',
      'focus',
      'blur',
      'input',
      'change',
      'submit',
      'reset',
      'contextmenu',
      'dragstart',
      'drag',
      'dragenter',
      'dragleave',
      'dragover',
      'drop',
      'dragend',
    ]

    eventTypes.forEach((eventType) => {
      currentRef.addEventListener(eventType, stopAllPropagation)
    })

    return () => {
      eventTypes.forEach((eventType) => {
        currentRef.removeEventListener(eventType, stopAllPropagation)
      })
    }
  }, [])

  return (
    <StyledWeekWrapper ref={broadcastTableRef}>
      {weekDays.map((week) => (
        <StyledWeek
          key={week.date.getDate()}
          isPrevDay={week.date.getDate() < currentDate.getDate()}
          isToday={week.date.getDate() === currentDate.getDate()}
          isNextDay={week.date.getDate() > currentDate.getDate()}
          disabled={week.isEmpty}
          isSelected={week.isSelected}
          tabIndex={0}
          onClick={() =>
            (week.date.getDate() === currentDate.getDate() || !week.isEmpty) &&
            handleClickWeek({ date: week.date, isEmpty: week.isEmpty })
          }
        >
          <StyledDay>{week.day}</StyledDay>
          <StyledDate>{week.date.getDate()}</StyledDate>
        </StyledWeek>
      ))}
    </StyledWeekWrapper>
  )
}

const StyledWeekWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  overflow-x: scroll;
  padding: 24px 0px;
  border-top: 1px solid #e6e6e6;
  border-bottom: 1px solid #e6e6e6;
  margin-bottom: 50px;
  @media screen and (max-width: 768px) {
    padding: 10px 16px;
    margin-bottom: 20px;
  }
`

const StyledWeek = styled.div<StyledWeekProps>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  padding: 8px;
  width: 56px;
  border-radius: 6px;
  cursor: pointer;
  ${({ disabled }) => disabled && `background-color: #fff; span { color: #b3b3b3; };`}
  ${({ isToday }) => isToday && `span { color: #FF2D55 }`}
  ${({ isSelected }) => isSelected && `background-color: #FF2D55; span { color: #fff; }`}
  ${({ isPrevDay, isSelected }) =>
    isPrevDay && isSelected && `background-color: #CCCCCC; span { color: #fff; }`}
    
  @media screen and (max-width: 768px) {
    padding: 10px;
  }
`

const StyledDay = styled.span`
  color: #000;
  font-size: 16px;
  font-weight: 500;
  line-height: 125%; /* 20px */
  @media screen and (max-width: 768px) {
    font-size: 10px;
  }
`

const StyledDate = styled.span`
  color: #000;
  font-size: 24px;
  font-weight: 700;
  line-height: 140%; /* 33.6px */
  @media screen and (max-width: 768px) {
    font-size: 18px;
  }
`

export default BroadcastWeekDays
